import { useCallback, useState } from 'react';
import type { SearchField, SortKey, TileKey, WarrantyFilter } from '../monitoringUtils';

export interface MonitoringFilters {
    q: string;
    qField: SearchField;
    types: string[];
    warranty: WarrantyFilter;
    withCampaign: boolean;
    tile: TileKey | null;
    accounts: string[];
    maintenanceGroups: string[];
    sort: { key: SortKey; dir: 1 | -1 };
    page: number;
    pageSize: number;
}

export type MonitoringView = 'delayed' | 'warrantyWithoutService' | 'withoutVolksTotal' | 'pendingCampaigns';

const initialFilters: MonitoringFilters = {
    q: '',
    qField: 'all',
    types: [],
    warranty: '',
    withCampaign: false,
    tile: null,
    accounts: [],
    maintenanceGroups: [],
    sort: { key: 'status', dir: 1 },
    page: 0,
    pageSize: 50,
};

const useMonitoringFilters = () => {
    const [filters, setFilters] = useState<MonitoringFilters>(initialFilters);

    const update = useCallback((patch: Partial<MonitoringFilters>) => {
        setFilters(prev => ({ ...prev, page: 0, ...patch }));
    }, []);

    const setPage = useCallback((page: number) => setFilters(prev => ({ ...prev, page })), []);

    const toggleSort = useCallback((key: SortKey) => {
        setFilters(prev => ({
            ...prev,
            page: 0,
            sort: prev.sort.key === key ? { key, dir: prev.sort.dir === 1 ? -1 : 1 } : { key, dir: 1 },
        }));
    }, []);

    const reset = useCallback(() => {
        setFilters(prev => ({ ...initialFilters, sort: prev.sort, pageSize: prev.pageSize }));
    }, []);

    const applyView = useCallback((view: MonitoringView) => {
        setFilters(prev => {
            const base = { ...initialFilters, sort: prev.sort, pageSize: prev.pageSize };
            switch (view) {
                case 'delayed':
                    return { ...base, tile: 'atrasada' };
                case 'warrantyWithoutService':
                    return { ...base, tile: 'conn0', warranty: 'in' };
                case 'withoutVolksTotal':
                    return { ...base, tile: 'vt0' };
                case 'pendingCampaigns':
                    return { ...base, withCampaign: true };
                default:
                    return base;
            }
        });
    }, []);

    const pageFiltersActive = filters.warranty !== '' || filters.withCampaign || filters.maintenanceGroups.length > 0;

    return { filters, update, setPage, toggleSort, reset, applyView, pageFiltersActive };
};

export default useMonitoringFilters;
